import type {
  EligibleSubscriptions,
  SubFamily,
  SupportedSubscriptions,
} from 'types/isomorphic/subscriptionClassification';

/**
 * Resolves the RF subscription sub-family that is both included in the asset
 * (supportedSubscriptions) and purchasable in the user's country
 * (eligibleSubscriptions). Creative trumps a tie. Returns null when no sub-family
 * is both included and purchasable — the asset then falls through to à-la-carte.
 *
 * Deliberately does NOT consult the asset's content `family`: an asset can be
 * included only in a subscription of a different family (e.g. a creative-family
 * asset included only in the editorial RF subscription), and we honour that.
 */
export const resolveSellableSubFamily = (
  supportedSubscriptions: SupportedSubscriptions | null | undefined,
  eligibleSubscriptions: EligibleSubscriptions | null | undefined
): SubFamily | null => {
  const creativeSellable = !!(
    supportedSubscriptions?.gettyCreativeRFSubscription &&
    eligibleSubscriptions?.gettyCreativeRFSubscription
  );
  const editorialSellable = !!(
    supportedSubscriptions?.gettyEditorialRFSubscription &&
    eligibleSubscriptions?.gettyEditorialRFSubscription
  );
  if (creativeSellable) return 'creative';
  if (editorialSellable) return 'editorial';
  return null;
};

export type SubscriptionOffer =
  | { kind: 'subscription'; subFamily: SubFamily }
  | { kind: 'alacarte' };

export const resolveSubscriptionOffer = (
  supported: SupportedSubscriptions | null | undefined,
  eligible: EligibleSubscriptions | null | undefined
): SubscriptionOffer => {
  const subFamily = resolveSellableSubFamily(supported, eligible);
  return subFamily ? { kind: 'subscription', subFamily } : { kind: 'alacarte' };
};
